import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { StaffMember } from '../../core/models/staff-member.model';
import { StaffConversation } from '../../core/models/staff-conversation.model';
import { Message } from '../../core/models/message.model';
import { StaffMessagingApiService } from '../../core/services/staff-messaging-api.service';
import { StaffChatService } from '../../core/services/staff-chat.service';
import { NotificationService } from '../../core/services/notification.service';
import { User } from '../../core/models/user.model';

@Component({
  selector: 'app-staff-messaging',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './staff-messaging.html',
  styleUrl: './staff-messaging.scss',
})
export class StaffMessagingComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageContainer') messageContainer!: ElementRef;

  colleagues: StaffMember[] = [];
  conversations: StaffConversation[] = [];
  activeConversation: StaffConversation | null = null;
  messages: Message[] = [];
  newMessage = '';

  colleaguesLoading = true;
  messagesLoading = false;
  sendingMessage = false;
  showColleaguesList = false;

  currentUser: User | null = null;
  private messageSubscription!: Subscription;
  private shouldScrollToBottom = false;

  constructor(
    private api: StaffMessagingApiService,
    private chatService: StaffChatService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    const profile = localStorage.getItem('userProfile');
    if (profile) {
      this.currentUser = JSON.parse(profile);
    }

    this.loadColleagues();
    this.loadConversations();
    this.subscribeToMessages();
    this.chatService.connect();
  }

  ngOnDestroy(): void {
    this.messageSubscription?.unsubscribe();
    this.typingSubscription?.unsubscribe();
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    this.chatService.setActiveConversation(null);
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  loadColleagues(): void {
    this.colleaguesLoading = true;
    this.api.getColleagues().subscribe({
      next: (res) => {
        this.colleagues = res;
        this.colleaguesLoading = false;
      },
      error: (err) => {
        this.notificationService.error(err?.error?.message || 'Failed to load colleagues');
        this.colleaguesLoading = false;
      },
    });
  }

  loadConversations(): void {
    this.api.getConversations().subscribe({
      next: (res) => {
        this.conversations = res;
      },
      error: () => {},
    });
  }

  openConversation(conversation: StaffConversation): void {
    this.activeConversation = conversation;
    this.chatService.setActiveConversation(conversation.id);
    this.loadMessages(conversation.id);
    this.showColleaguesList = false;
  }

  startChatWith(colleague: StaffMember): void {
    this.api.getOrCreateConversation(colleague.id).subscribe({
      next: (conv) => {
        const existing = this.conversations.find((c) => c.id === conv.id);
        if (!existing) {
          this.conversations = [conv, ...this.conversations];
        }
        this.openConversation(conv);
      },
      error: (err) => {
        this.notificationService.error(err?.error?.message || 'Failed to start chat');
      },
    });
  }

  loadMessages(conversationId: string): void {
    this.messagesLoading = true;
    this.messages = [];
    this.api.getMessages(conversationId).subscribe({
      next: (res) => {
        this.messages = res;
        this.messagesLoading = false;
        this.shouldScrollToBottom = true;
      },
      error: (err) => {
        this.notificationService.error(err?.error?.message || 'Failed to load messages');
        this.messagesLoading = false;
      },
    });
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || !this.activeConversation) return;

    const message: Message = {
      id: null,
      content: this.newMessage.trim(),
      senderId: this.currentUser?.id,
      receiverId: this.activeConversation.otherUserId,
      conversationId: this.activeConversation.id,
      sentTime: null,
      bookingId: undefined,
    };

    this.newMessage = '';
    this.chatService.sendMessage(message);
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  isMe(msg: Message): boolean {
    return msg.senderId === this.currentUser?.id;
  }

  getInitials(member: StaffMember | StaffConversation): string {
    if ('firstName' in member) {
      return (member.firstName.charAt(0) + member.lastName.charAt(0)).toUpperCase();
    }
    const parts = member.title?.split(' ') ?? [];
    return parts.length >= 2
      ? (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
      : (parts[0]?.charAt(0).toUpperCase() ?? '?');
  }

  getRoleLabel(role: string): string {
    switch (role) {
      case 'DOCTOR':
        return 'Doctor';
      case 'MANAGER':
        return 'Manager';
      case 'RECEPTION':
        return 'Receptionist';
      default:
        return role;
    }
  }

  private typingSubscription!: Subscription;
  private typingTimeout: any;
  private lastTypingSent = 0;
  isPartnerTyping = false;
  partnerTypingName = '';

  private subscribeToMessages(): void {
    this.messageSubscription = this.chatService.messages$.subscribe((msg) => {
      if (msg.conversationId === this.activeConversation?.id) {
        this.messages = [...this.messages, msg];
        this.shouldScrollToBottom = true;
        // If we get a message, hide typing indicator immediately
        if (msg.senderId !== this.currentUser?.id) {
          this.clearTypingIndicator();
        }
      } else {
        // Update the last message preview in the conversation list
        const conv = this.conversations.find((c) => c.id === msg.conversationId);
        if (conv) {
          conv.lastMessage = msg.content;
          conv.lastMessageTime = msg.sentTime ?? undefined;
        }
      }
    });

    this.typingSubscription = this.chatService.typingEvents$.subscribe((event) => {
      if (event.conversationId === this.activeConversation?.id && event.senderId !== this.currentUser?.id) {
        this.isPartnerTyping = true;
        this.partnerTypingName = event.senderName;
        this.shouldScrollToBottom = true;
        
        if (this.typingTimeout) {
          clearTimeout(this.typingTimeout);
        }
        
        this.typingTimeout = setTimeout(() => {
          this.clearTypingIndicator();
        }, 3000);
      }
    });
  }

  private clearTypingIndicator(): void {
    this.isPartnerTyping = false;
    this.partnerTypingName = '';
  }

  onInput(): void {
    if (!this.activeConversation || !this.currentUser) return;
    
    const now = Date.now();
    if (now - this.lastTypingSent > 2000) {
      this.lastTypingSent = now;
      this.chatService.sendTypingEvent(
        this.activeConversation.otherUserId, 
        this.activeConversation.id, 
        `${this.currentUser.firstName} ${this.currentUser.lastName}`
      );
    }
  }

  private scrollToBottom(): void {
    try {
      const el = this.messageContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }
}
